import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { logger } from '../../utils/logger';

interface Company {
  company_id: string;
  company_name: string;
  display_name?: string;
  tier: string;
  status: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  user_count: number;
}

const CompanyList: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'activate' | 'deactivate' | null;
    company: Company | null;
  }>({ open: false, action: null, company: null });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/companies');

      setCompanies(response.data.companies);
      setError(null);
    } catch (err: any) {
      logger.error('Fetch companies error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to fetch companies';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateDeactivate = async () => {
    if (!confirmDialog.company || !confirmDialog.action) return;

    try {
      setActionLoading(true);
      const endpoint = confirmDialog.action === 'activate' ? 'activate' : 'deactivate';

      await api.patch(
        `/admin/companies/${confirmDialog.company.company_id}/${endpoint}`,
        {}
      );

      // Refresh companies list
      await fetchCompanies();
      setConfirmDialog({ open: false, action: null, company: null });
      setError(null);
    } catch (err: any) {
      logger.error('Action error:', err);
      const errorMessage = err.response?.data?.detail || 'Action failed';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmDialog = (action: 'activate' | 'deactivate', company: Company) => {
    setConfirmDialog({ open: true, action, company });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null, company: null });
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return 'default';
      case 'BASIC':
        return 'primary';
      case 'PROFESSIONAL':
        return 'secondary';
      case 'ENTERPRISE':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1">
            Upravljanje Kompanijama
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/companies/create')}
        >
          Kreiraj Kompaniju
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Naziv Kompanije</strong></TableCell>
                  <TableCell><strong>Paket</strong></TableCell>
                  <TableCell align="center"><strong>Korisnici</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Kreirano</strong></TableCell>
                  <TableCell align="center"><strong>Akcije</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nema kreiranih kompanija. Kreirajte prvu kompaniju!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow key={company.company_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {company.display_name || company.company_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {company.company_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.tier}
                          color={getTierColor(company.tier) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {company.user_count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.is_active ? 'Aktivna' : 'Neaktivna'}
                          color={company.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(company.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/companies/${company.company_id}`)}
                          title="Prikaži Detalje"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/admin/companies/${company.company_id}/edit`)}
                          title="Ažuriraj"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {company.is_active ? (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openConfirmDialog('deactivate', company)}
                            title="Deaktiviraj"
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => openConfirmDialog('activate', company)}
                            title="Aktiviraj"
                          >
                            <ActivateIcon fontSize="small" />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>
          {confirmDialog.action === 'deactivate' ? 'Deaktiviraj Kompaniju' : 'Aktiviraj Kompaniju'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'deactivate' ? (
              <>
                Da li ste sigurni da želite da deaktivirate kompaniju{' '}
                <strong>{confirmDialog.company?.display_name || confirmDialog.company?.company_name}</strong>?
                <br /><br />
                Svi korisnici ove kompanije će biti blokirani.
              </>
            ) : (
              <>
                Da li ste sigurni da želite da aktivirate kompaniju{' '}
                <strong>{confirmDialog.company?.display_name || confirmDialog.company?.company_name}</strong>?
                <br /><br />
                Svi korisnici ove kompanije će biti ponovo aktivirani.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={actionLoading}>
            Otkaži
          </Button>
          <Button
            onClick={handleActivateDeactivate}
            color={confirmDialog.action === 'deactivate' ? 'error' : 'success'}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={20} />
            ) : confirmDialog.action === 'deactivate' ? (
              'Deaktiviraj'
            ) : (
              'Aktiviraj'
            )}
          </Button>
        </DialogActions>
      </Dialog>
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyList;
